import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, Tag, Typography, Space, Modal, message, Popconfirm, Tooltip, Card, Row, Col, Statistic, Divider, Input, Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import { DeleteOutlined, PlusOutlined, EyeOutlined, FileExcelOutlined } from '@ant-design/icons';
import CampaignAuditForm from '../components/inventoryAudit/CampaignAuditForm';

// Import clean code constants
import { INVENTORY_AUDIT_STATUS, STATUS_LABELS, STATUS_COLORS } from '../constants/inventoryAudit';
import AuditErrorBoundary from '../components/common/AuditErrorBoundary';

const { Search } = Input;
const { Option } = Select;

const { Title } = Typography;

// Use centralized types instead of local constants
type AuditStatus = string;

interface InventoryAudit {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  status: AuditStatus;
  created_by_user_id: number;
  company_id: number;
  created_at: string;
  updated_at: string;
}

interface User {
  id: number;
  full_name: string;
  username?: string;
  company_id?: number;
  role?: string;
}

const InventoryAuditListPage: React.FC = () => {
  const [audits, setAudits] = useState<InventoryAudit[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
  });

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const navigate = useNavigate();

  // Helper functions
  const getCurrentUser = (): User => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {} as User;
    }
  };

  const parseApiResponse = <T,>(data: any): T[] => {
    if (Array.isArray(data)) {
      return data;
    }
    if (data?.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  };

  // Computed values
  const auditStats = useMemo(() => {
    const total = audits.length;
    const inProgress = audits.filter(a => a.status === INVENTORY_AUDIT_STATUS.IN_PROGRESS).length;
    const pendingApproval = audits.filter(a => a.status === INVENTORY_AUDIT_STATUS.PENDING_APPROVAL).length;
    const completed = audits.filter(a => a.status === INVENTORY_AUDIT_STATUS.COMPLETED).length;
    const pendingSetup = audits.filter(a => a.status === INVENTORY_AUDIT_STATUS.PENDING_SETUP).length;

    return {
      total,
      inProgress,
      pendingApproval,
      completed,
      pendingSetup,
    };
  }, [audits]);

  // API functions
  const reloadAudits = async () => {
    setLoading(true);
    try {
      const data = await apiClient.getInventoryAudits();
      const auditsArr = parseApiResponse<InventoryAudit>(data);
      // Backend now automatically filters by company for company_admin/manager
      setAudits(auditsArr);
    } catch (error) {
      console.error('Error loading audits:', error);
      message.error('Lỗi khi tải danh sách kiểm kê');
    } finally {
      setLoading(false);
    }
  };





  // loadUsersByCompany không cần thiết cho manager/company_admin
  // const loadUsersByCompany = async (companyId: number) => {
  //   try {
  //     const users = await apiClient.getUsers();
  //     const allUsers = parseApiResponse<User>(users);
  //     const filteredUsers = allUsers.filter(u => u.company_id === companyId);
  //     setUserList(filteredUsers);
  //   } catch (error) {
  //     console.error('Error loading users:', error);
  //   }
  // };

  // Event handlers
  const handleOpenModal = async () => {
    setShowModal(true);
    // User permissions handled in CampaignAuditForm
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleStatusFilter = (value: string) => {
    setFilters(prev => ({ ...prev, status: value }));
  };





  const handleDeleteAudit = async (auditId: number) => {
    try {
      await apiClient.deleteInventoryAudit(auditId);
      message.success('Đã xóa đợt kiểm kê!');
      await reloadAudits();
    } catch (err) {
      console.error('Error deleting audit:', err);
      message.error('Xóa đợt kiểm kê thất bại!');
    }
  };

  // Effects
  useEffect(() => {
    reloadAudits();
  }, []); // reloadAudits will be stable after first render

  useEffect(() => {
    const user = getCurrentUser();
    const isSuperAdminUser = user.role === 'super_admin';
    setIsSuperAdmin(isSuperAdminUser);
  }, []);

  // Table columns
  const columns = [
    {
      title: 'Tên đợt kiểm kê',
      dataIndex: 'name',
      key: 'name',
      align: 'left' as const,
      ellipsis: true,
      render: (text: string) => <b>{text}</b>,
    },
    {
      title: 'Ngày bắt đầu',
      dataIndex: 'start_date',
      key: 'start_date',
      align: 'center' as const,
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString('vi-VN'),
    },
    {
      title: 'Ngày kết thúc',
      dataIndex: 'end_date',
      key: 'end_date',
      align: 'center' as const,
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString('vi-VN'),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      align: 'center' as const,
      width: 140,
      render: (status: AuditStatus) => {
        // Map legacy status values to new constants  
        let mappedStatus = status;
        if (status === 'Chờ duyệt thực hiện') mappedStatus = INVENTORY_AUDIT_STATUS.PENDING_SETUP;
        if (status === 'Đang tiến hành') mappedStatus = INVENTORY_AUDIT_STATUS.IN_PROGRESS;
        if (status === 'Chờ duyệt hoàn thành') mappedStatus = INVENTORY_AUDIT_STATUS.PENDING_APPROVAL;
        if (status === 'Hoàn thành') mappedStatus = INVENTORY_AUDIT_STATUS.COMPLETED;

        const color = STATUS_COLORS[mappedStatus as keyof typeof STATUS_COLORS] || 'default';
        const label = STATUS_LABELS[mappedStatus as keyof typeof STATUS_LABELS] || status;

        return (
          <Tag color={color}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'Hành động',
      key: 'actions',
      align: 'center' as const,
      width: 120,
      render: (_: any, record: InventoryAudit) => (
        <Space>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/inventory-audit/${record.id}`)}
            />
          </Tooltip>
          {isSuperAdmin && (
            <Popconfirm
              title="Bạn có chắc muốn xóa đợt kiểm kê này?"
              onConfirm={() => handleDeleteAudit(record.id)}
              okText="Xóa"
              cancelText="Hủy"
            >
              <Tooltip title="Xóa đợt kiểm kê">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, display: 'inline-block' }}>
          Kiểm kê tài sản
        </Title>
        {getCurrentUser()?.role !== 'employee_user' && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenModal}
            style={{ float: 'right' }}
            size="large"
          >
            Tạo đợt kiểm kê
          </Button>
        )}
      </div>

      {/* Thống kê - áp dụng UI trang thanh lý */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Tổng đợt kiểm kê" value={auditStats.total} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Đang tiến hành" value={auditStats.inProgress} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Chờ phê duyệt" value={auditStats.pendingApproval} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Hoàn thành" value={auditStats.completed} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      {/* Filter section - giống trang điều chuyển và thanh lý */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Search
              placeholder="Tìm kiếm theo tên đợt kiểm kê..."
              onSearch={handleSearch}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Trạng thái"
              allowClear
              style={{ width: '100%' }}
              onChange={handleStatusFilter}
            >
              <Option value="pending_setup">Chờ thiết lập</Option>
              <Option value="pending_approval">Chờ phê duyệt</Option>
              <Option value="in_progress">Đang tiến hành</Option>
              <Option value="completed">Hoàn thành</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Button icon={<FileExcelOutlined />} onClick={() => message.info('Tính năng xuất Excel đang được phát triển')}>
              Xuất Excel
            </Button>
          </Col>
        </Row>
      </Card>

      <Card title={`Danh sách đợt kiểm kê tài sản (${auditStats.total})`}>
        <Table
          dataSource={audits}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} đợt kiểm kê`,
          }}
        />
      </Card>

      <Modal
        open={showModal}
        title={null}
        onCancel={() => setShowModal(false)}
        footer={null}
        width={700}
        destroyOnClose
      >
        <CampaignAuditForm
          onSubmit={async (values) => {
            setLoading(true);
            try {
              await apiClient.createCampaignAudit(values);
              message.success('Tạo đợt kiểm kê thành công');
              setShowModal(false);
              await reloadAudits();
            } catch (error: any) {
              console.error('Error creating audit:', error);
              const errorMessage = error?.response?.data?.message || error?.message || 'Lỗi khi tạo đợt kiểm kê';

              // Hiển thị error với format đẹp hơn
              message.error({
                content: (
                  <div style={{ whiteSpace: 'pre-line', maxWidth: '400px' }}>
                    {errorMessage}
                  </div>
                ),
                duration: 10, // Hiển thị lâu hơn để user đọc được message dài
                style: {
                  marginTop: '20vh',
                },
              });
            } finally {
              setLoading(false);
            }
          }}
          loading={loading}
        />
      </Modal>
    </div>
  );
};

// Wrap with Error Boundary for better error handling
const InventoryAuditListPageWithErrorBoundary: React.FC = () => (
  <AuditErrorBoundary>
    <InventoryAuditListPage />
  </AuditErrorBoundary>
);

export default InventoryAuditListPageWithErrorBoundary; 